import { CustomPage } from '@common/admin/custom-pages/custom-page';
import { apiClient } from '@common/http/query-client';
import { toast } from '@common/ui/toast/toast';
import { useCallback, useMemo, useState } from 'react';

interface CustomPageBodyProps {
  page: CustomPage;
}

const DOWNLOAD_ICON_SRC = '/images/drive-new/icon-download.svg';

function isLegalDocPage(page: CustomPage): boolean {
  const t = page.title?.toLowerCase() ?? '';
  return (
    t.includes('privacy policy') ||
    page.slug === 'privacy-policy' ||
    t.includes('cookie policy') ||
    page.slug === 'cookie-policy' ||
    t.includes('updates & news') ||
    t.includes('updates and news') ||
    page.slug === 'updates-news' ||
    t.includes('terms of service') ||
    t.includes('terms of use') ||
    page.slug === 'terms-of-service'
  );
}

/**
 * For the updates-news page, extract:
 * - dateHtml: the first <h2> that has a color style (the red date line)
 * - articleTitle: the first <h3> text (the article heading)
 * - remainingBody: the body with h1, the date h2, and the article h3 stripped out
 */
function extractUpdatesNewsParts(body: string): {
  dateHtml: string;
  articleTitle: string;
  remainingBody: string;
} {
  let remaining = body;

  // Strip leading <h1>
  remaining = remaining.replace(/^\s*<h1[^>]*>[\s\S]*?<\/h1>\s*/i, '');

  // Extract the first <h2> with a color style (the date)
  const dateMatch = remaining.match(/<h2[^>]*style[^>]*color[^>]*>([\s\S]*?)<\/h2>/i);
  const dateHtml = dateMatch ? dateMatch[0] : '';
  if (dateMatch) {
    remaining = remaining.replace(dateMatch[0], '');
  }

  // Extract the first <h3> (article title)
  const h3Match = remaining.match(/<h3[^>]*>([\s\S]*?)<\/h3>/i);
  const articleTitle = h3Match ? h3Match[1].replace(/<[^>]+>/g, '').trim() : '';
  if (h3Match) {
    remaining = remaining.replace(h3Match[0], '');
  }

  return { dateHtml, articleTitle, remainingBody: remaining.trim() };
}

function getAppBaseUrl(): string {
  const baseAttr = document.querySelector('base')?.getAttribute('href') ?? '/';
  return new URL(baseAttr, window.location.origin).href;
}

function resolveAppUrl(path: string): string {
  return new URL(path.replace(/^\//, ''), getAppBaseUrl()).href;
}

async function isPdfBlob(blob: Blob): Promise<boolean> {
  const header = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
  return (
    header[0] === 0x25 &&
    header[1] === 0x50 &&
    header[2] === 0x44 &&
    header[3] === 0x46
  );
}

async function downloadLegalPdf(slug: string): Promise<void> {
  const tryUrls = [
    resolveAppUrl(`legal/${encodeURIComponent(slug)}.pdf`),
    resolveAppUrl(`api/v1/custom-pages/${encodeURIComponent(slug)}/pdf`),
  ];

  let lastError: unknown;

  for (const url of tryUrls) {
    try {
      const response = await fetch(url, {
        credentials: 'include',
        headers: {Accept: 'application/pdf'},
      });

      if (!response.ok) {
        throw new Error(`PDF download failed (${response.status})`);
      }

      const blob = await response.blob();

      if (!(await isPdfBlob(blob))) {
        throw new Error('Server did not return a PDF file');
      }

      const objectUrl = URL.createObjectURL(
        new Blob([blob], {type: 'application/pdf'}),
      );
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${slug}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      return;
    } catch (error) {
      lastError = error;
    }
  }

  // Same URL resolution the rest of the app uses for custom-pages API.
  try {
    const response = await apiClient.get(
      `custom-pages/${encodeURIComponent(slug)}/pdf`,
      {
        responseType: 'blob',
        headers: {Accept: 'application/pdf'},
      },
    );
    const blob = response.data as Blob;

    if (!(await isPdfBlob(blob))) {
      throw new Error('Server did not return a PDF file');
    }

    const objectUrl = URL.createObjectURL(
      new Blob([blob], {type: 'application/pdf'}),
    );
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `${slug}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    return;
  } catch (error) {
    lastError = error;
  }

  throw lastError ?? new Error('PDF download failed');
}

export function CustomPageBody({ page }: CustomPageBodyProps) {
  const legal = isLegalDocPage(page);
  const isUpdatesNewsPage = page.slug === 'updates-news';
  const bodyStartsWithHeading = /^\s*<h1[\s>]/i.test(page.body || '');
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const updatesNewsParts = useMemo(() => {
    if (!isUpdatesNewsPage) return null;
    return extractUpdatesNewsParts(page.body || '');
  }, [isUpdatesNewsPage, page.body]);

  const handleDownloadPdf = useCallback(async () => {
    if (isDownloadingPdf || !page.slug) return;

    setIsDownloadingPdf(true);
    try {
      await downloadLegalPdf(page.slug);
    } catch (error) {
      console.error('Failed to download legal PDF', error);
      toast('Could not download PDF. Please try again later.');
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [isDownloadingPdf, page.slug]);

  if (legal) {
    return (
      <div className="w-full bg-ct-bg px-16 pb-80 pt-28 dark:bg-[#14161a] md:px-24 md:pt-36 lg:px-42 lg:pt-44 xl:pt-52">
        <div className="mx-auto w-full max-w-[1560px] [font-family:Inter,ui-sans-serif,system-ui,-apple-system,sans-serif]">
          <article className="relative isolate w-full">
            {/* Gray background layer — header band */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-[889px] rounded-[60px] border-2 border-ct-panel bg-ct-panel dark:border-[#2a2d33] dark:bg-[#1d2128]" />

            {/* Header: title + date (for updates-news) */}
            <header className="relative z-[2] px-24 pt-20 md:px-[54px] md:pt-[39px]">
              <div className="flex w-full flex-col gap-8 sm:flex-row sm:items-center sm:justify-between sm:gap-24">
                <h1 className="min-w-0 shrink text-ct-title font-bold leading-[48px] text-ct-text dark:text-[#f3f4f6]">
                  {page.title}
                </h1>
                {!isUpdatesNewsPage && (
                  <button
                    type="button"
                    onClick={handleDownloadPdf}
                    disabled={isDownloadingPdf}
                    className="flex h-60 w-60 flex-shrink-0 items-center justify-center rounded-[24px] bg-white no-tap-highlight shadow-sm transition hover:bg-white/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-wait disabled:opacity-70 dark:bg-[#232832] dark:hover:bg-[#2f3440]"
                    aria-label="Download as PDF"
                    aria-busy={isDownloadingPdf}
                  >
                    <img
                      src={DOWNLOAD_ICON_SRC}
                      alt=""
                      width={30}
                      height={30}
                      className="size-30"
                    />
                  </button>
                )}
              </div>
              {/* Date line sits inside the gray header band, below the title */}
              {isUpdatesNewsPage && updatesNewsParts?.dateHtml && (
                <div
                  className="mt-8 text-[22px] font-bold leading-tight [&_h2]:m-0 [&_h2]:text-[22px] [&_h2]:font-bold [&_h2]:leading-tight"
                  dangerouslySetInnerHTML={{ __html: updatesNewsParts.dateHtml }}
                />
              )}
            </header>

            {/* White card */}
            <div className="relative z-[1] mt-[31px] w-full rounded-[60px] border-2 border-ct-panel bg-white px-18 pb-80 pt-48 dark:border-[#2a2d33] dark:bg-[#232832] md:mt-[23px] md:px-32 md:pt-[54px]">
              {/* Article title: bold, flush to the left corner of the white card */}
              {isUpdatesNewsPage && updatesNewsParts?.articleTitle && (
                <h2 className="mb-24 text-[22px] font-bold leading-snug text-ct-text dark:text-[#f3f4f6]">
                  {updatesNewsParts.articleTitle}
                </h2>
              )}
              <div
                className={`legal-doc-html prose prose-base mx-auto max-w-[875px] break-words text-base font-normal leading-normal text-ct-text dark:prose-invert dark:text-[#b3bcc8] ${bodyStartsWithHeading ? '[&_h1:first-of-type]:hidden' : ''}`}
                dangerouslySetInnerHTML={{
                  __html: isUpdatesNewsPage && updatesNewsParts
                    ? updatesNewsParts.remainingBody
                    : page.body,
                }}
              />
            </div>
          </article>
        </div>
      </div>
    );
  }

  return (
    <div className="px-16 md:px-24">
      <div className="prose dark:prose-invert mx-auto my-50" style={{ maxWidth: '80ch' }}>
        {!bodyStartsWithHeading && <h1>{page.title}</h1>}
        <div
          className="break-words whitespace-pre-wrap"
          dangerouslySetInnerHTML={{ __html: page.body }}
        />
      </div>
    </div>
  );
}
